/**
 * <s-gallery-thumbs> — mobile thumbnail strip for the PDP media scroller.
 *
 * Required markup:
 *   [data-gallery-target] attribute on the element — id of the horizontal scroller.
 *   button[data-thumb-index] — one per slide; scrolls the scroller to that slide.
 */
class SGalleryThumbs extends HTMLElement {
  #ac;
  #scroller;
  #raf = 0;

  connectedCallback() {
    this.#scroller = document.getElementById(this.dataset.galleryTarget || '');
    if (!this.#scroller) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleClick, opts);
    this.#scroller.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  get #slides() {
    return Array.from(this.#scroller.children);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-thumb-index]');
    if (!button || !this.contains(button)) return;

    const slide = this.#slides[Number(button.dataset.thumbIndex)];
    if (!slide) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#scroller.scrollTo({ left: slide.offsetLeft - this.#scroller.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const width = this.#scroller.clientWidth || 1;
    const active = Math.round(this.#scroller.scrollLeft / width);
    for (const button of this.querySelectorAll('button[data-thumb-index]')) {
      const isActive = Number(button.dataset.thumbIndex) === active;
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-gallery-thumbs')) {
  customElements.define('s-gallery-thumbs', SGalleryThumbs);
}
